import toStringValues from "../util/enum"

export enum ACH_RELATIONSHIP_STATUSES {
  PRE_SEND = "PRE_SEND",
  REJECTED = "REJECTED",
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  CANCELED = "CANCELED",
  IDENTITY_FAIL = "IDENTITY_FAIL",
  IDENTITY_MISSING = "IDENTITY_MISSING",
  PREVIOUS_ACH_RETURNED_R10 = "PREVIOUS_ACH_RETURNED_R10",
  NEEDS_BROKER_REVIEW = "NEEDS_BROKER_REVIEW",
}

export const ACH_RELATIONSHIP_NEED_REVIEW_STATUSES = [
  ACH_RELATIONSHIP_STATUSES.IDENTITY_FAIL,
  ACH_RELATIONSHIP_STATUSES.IDENTITY_MISSING,
  ACH_RELATIONSHIP_STATUSES.NEEDS_BROKER_REVIEW,
  ACH_RELATIONSHIP_STATUSES.PREVIOUS_ACH_RETURNED_R10,
]

export enum RELATIONSHIP_METHODS {
  MICRO_DEPOSIT = "MICRO_DEPOSIT",
  PLAID = "PLAID",
}

export enum BANK_ACCOUNT_TYPES {
  CHECKING = "CHECKING",
  SAVINGS = "SAVINGS",
}

export enum INSTITUTION_STATUSES {
  CONNECTED = "Connected",
  REAUTH_REQUIRED = "Reauth Required",
  CANCELED = "Canceled",
}

export enum PRE_REGISTERED_DEPOSIT_STATUSES {
  CANCELED = "Canceled",
  AWAITING_ACH_RELATIONSHIP_CREATION = "Awaiting ACH Relationship Creation",
  SUBMITTED_TO_CLEARING = "Submitted to Clearing",
}

export enum BANKING_PRE_REGISTRATION_ACCOUNT_TYPES {
  CHECKING = "Checking",
  SAVINGS = "Savings",
}

export enum BANKING_PRE_REGISTRATION_STATUSES {
  CANCELED = "Canceled",
  AWAITING_ACCOUNT_CREATION = "Awaiting Account Creation",
  SUBMITTED_TO_CLEARING = "Submitted to Clearing",
  ERROR = "Error",
}

export enum TRANSFER_DIRECTIONS {
  BLANK = "",
  OUTGOING = "OUTGOING",
  INCOMING = "INCOMING",
}

export enum TRANSFER_STATES {
  /** has not yet been sent. FE-only */
  BLANK = "",
  PRE_SEND = "PRE_SEND",
  /** used for RTP */
  ACTION_REQUIRED = "ACTION_REQUIRED",
  SEND_FAILED = "SEND_FAILED",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
  CANCEL_REQUESTED = "CANCEL_REQUESTED",
  COMPLETE = "COMPLETE",
  FUNDS_POSTED = "FUNDS_POSTED",
  PENDING = "PENDING",
  /** this is a chase only status that will be resolved once the user reauths with that bank */
  REAUTH_REQUIRED = "REAUTH_REQUIRED",
  REJECTED = "REJECTED",
  RETURNED = "RETURNED", // ACHs only
}

export enum PLATFORM_NAMES {
  ANDROID = "Android",
  IOS = "iOS",
  WEB = "Web",
}

export enum TRANSFER_METHODS {
  BLANK = "",
  ACH = "ACH",
  RTP = "RTP",
  WIRE = "WIRE",
  CHECK = "CHECK",
  STABLECOIN = "STABLECOIN",
}

export enum IRA_CONTRIBUTION_REASONS {
  CONVERSION = "CONVERSION",
  EMPLOYEE = "EMPLOYEE",
  EMPLOYER = "EMPLOYER",
  RECHARACTERIZATION = "RECHARACTERIZATION",
  REGULAR = "REGULAR",
  ROLLOVER_60_DAY = "ROLLOVER_60_DAY",
  ROLLOVER_DIRECT = "ROLLOVER_DIRECT",
  TRANSFER = "TRANSFER",
}

export enum IRA_DISTRIBUTION_REASONS {
  CONVERSION = "CONVERSION",
  DEATH = "DEATH",
  EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE = "EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE",
  EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE = "EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE",
  NORMAL = "NORMAL",
  PREMATURE = "PREMATURE",
  RECHARACTERIZATION_PRIOR_YEAR = "RECHARACTERIZATION_PRIOR_YEAR",
  RECHARACTERIZATION_CURRENT_YEAR = "RECHARACTERIZATION_CURRENT_YEAR",
  TRANSFER = "TRANSFER",
}

export enum RECURRING_DEPOSIT_STATUSES {
  ACTIVE = "Active",
  CANCELED = "Canceled",
  COMPLETE = "Complete",
  PRE_SEND = "Pre-Send",
  SEND_FAILED = "Send Failed",
}

export enum RECURRING_PERIODS {
  DAY_OF_MONTH = "Day of Month",
  DAYS = "Days",
  ONE_TIME_TRANSACTION = "One Time Transaction",
  WEEKS = "Weeks",
}

export enum DEPOSIT_FREQUENCIES {
  ONE_TIME = "One Time",
  DAILY = "Daily",
  WEEKLY = "Weekly",
  BIWEEKLY = "Biweekly",
  MONTHLY = "Monthly",
}

export enum TransactionStatusBucket {
  PROCESSING = "processing",
  REJECTED = "rejected",
  CANCELED = "canceled",
  COMPLETE = "complete",
  RETURNED = "returned",
  ACTION_REQUIRED = "actionRequired",
}

export enum PendingCashEntryState {
  ACTIVE = "Active",
  EXPIRED = "Expired",
  RELEASED = "Released",
}

// Derived constants
export const DEPOSIT_TRANSFER_METHODS: readonly string[] = Object.freeze([
  TRANSFER_METHODS.ACH,
  TRANSFER_METHODS.RTP,
])

export const TERMINAL_TRANSFER_STATES: readonly string[] = Object.freeze([
  TRANSFER_STATES.CANCEL_REQUESTED,
  TRANSFER_STATES.CANCELED,
  TRANSFER_STATES.COMPLETE,
  TRANSFER_STATES.REJECTED,
  TRANSFER_STATES.RETURNED,
  TRANSFER_STATES.SEND_FAILED,
])

export const PENDING_TRANSFER_STATES: readonly string[] = Object.freeze([
  TRANSFER_STATES.FUNDS_POSTED,
  TRANSFER_STATES.PENDING,
  TRANSFER_STATES.PRE_SEND,
])

export const PRE_REGISTERED_DEPOSIT_TERMINAL_STATES: readonly string[] =
  Object.freeze([
    PRE_REGISTERED_DEPOSIT_STATUSES.CANCELED,
    PRE_REGISTERED_DEPOSIT_STATUSES.SUBMITTED_TO_CLEARING,
  ])

export const RECURRING_PERIOD_VALUES = toStringValues(RECURRING_PERIODS)
export const DEPOSIT_FREQUENCY_VALUES = toStringValues(DEPOSIT_FREQUENCIES)
export const IRA_DISTRIBUTION_REASON_VALUES = toStringValues(
  IRA_DISTRIBUTION_REASONS
)

export const ACH_DEPOSIT_MAXIMUM = 250_000
export const TRANSACTION_REAUTH_REQUIRED_REASON =
  "Your login credentials at your bank have changed. Please re-authenticate with your new credentials."

export const RECURRING_DEPOSIT_ELIGIBLE_REASONS = [
  IRA_CONTRIBUTION_REASONS.REGULAR,
  IRA_CONTRIBUTION_REASONS.EMPLOYEE,
  IRA_CONTRIBUTION_REASONS.EMPLOYER,
  IRA_CONTRIBUTION_REASONS.TRANSFER,
]

// Recurring deposit frequency logic
const RECURRING_FREQUENCY_DATA = {
  [DEPOSIT_FREQUENCIES.ONE_TIME]: {
    period: null,
    interval: 1,
  },
  [DEPOSIT_FREQUENCIES.DAILY]: {
    period: RECURRING_PERIODS.DAYS,
    interval: 1,
  },
  [DEPOSIT_FREQUENCIES.WEEKLY]: {
    period: RECURRING_PERIODS.WEEKS,
    interval: 1,
  },
  [DEPOSIT_FREQUENCIES.BIWEEKLY]: {
    period: RECURRING_PERIODS.WEEKS,
    interval: 2,
  },
  [DEPOSIT_FREQUENCIES.MONTHLY]: {
    period: RECURRING_PERIODS.DAY_OF_MONTH,
    interval: 1,
  },
}

export function getPeriodFromFrequency(frequency: DEPOSIT_FREQUENCIES) {
  const frequencyData = RECURRING_FREQUENCY_DATA[frequency]
  return frequencyData.period
}

export function getIntervalFromFrequency(frequency: DEPOSIT_FREQUENCIES) {
  const frequencyData = RECURRING_FREQUENCY_DATA[frequency]
  return frequencyData.interval
}

export function getFrequencyFromPeriodAndInterval(
  period: RECURRING_PERIODS | null,
  interval: number
) {
  return Object.keys(RECURRING_FREQUENCY_DATA).find((frequency) => {
    const frequencyData =
      RECURRING_FREQUENCY_DATA[frequency as DEPOSIT_FREQUENCIES]
    return (
      frequencyData.period === period && frequencyData.interval === interval
    )
  })
}

export function getStatusBucket(
  state: TRANSFER_STATES | string
): TransactionStatusBucket {
  switch (state) {
    case TRANSFER_STATES.REJECTED:
    case TRANSFER_STATES.SEND_FAILED:
    case TRANSFER_STATES.FAILED:
      return TransactionStatusBucket.REJECTED
    case TRANSFER_STATES.RETURNED:
      return TransactionStatusBucket.RETURNED
    case TRANSFER_STATES.CANCELED:
    case TRANSFER_STATES.CANCEL_REQUESTED:
      return TransactionStatusBucket.CANCELED
    case TRANSFER_STATES.COMPLETE:
      return TransactionStatusBucket.COMPLETE
    case TRANSFER_STATES.ACTION_REQUIRED:
      return TransactionStatusBucket.ACTION_REQUIRED
    case TRANSFER_STATES.PRE_SEND:
    case TRANSFER_STATES.PENDING:
    case TRANSFER_STATES.FUNDS_POSTED:
    case TRANSFER_STATES.REAUTH_REQUIRED:
    case TRANSFER_STATES.BLANK:
    default:
      return TransactionStatusBucket.PROCESSING
  }
}
